import * as t from 'io-ts';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {makeEnumRuntimeType, RCalendarEventVersionUidRef} from '../../../models/api/apiTypes';
import {RCalendarEventVersionStartEnd} from '../../../models/api/calendarEventVersionModel';
import {RCalendarVersionIdRef} from '../../../models/api/calendarVersionModel';
import {RAsyncStatus} from '../../appTypes';
import {RPanelCancelTokenId} from '../panelCancelTokenId';
import {PanelTypesEnum, RCalendarsPanelId, RPanelStateBase} from '../panelStateBase';
import {RCalendarsParameters} from './calendarsParameters';
import {RCalendarsPanelSchedulingState} from './scheduling/calendarsPanelSchedulingState';

/*
 * Runtime types.
 */

export enum CalendarViewShiftDirectionsEnum {
  NEXT = 'NEXT',
  PREVIOUS = 'PREVIOUS',
}

export enum CalendarsModesEnum {
  CALENDAR_GRID = 'CALENDAR_GRID',
  CALENDAR_PREVIEW = 'CALENDAR_PREVIEW',
  SELECT_CALENDARS = 'SELECT_CALENDARS',
  VIEW_EVENT = 'VIEW_EVENT',
  EDIT_EVENT = 'EDIT_EVENT',
  REMINDERS = 'REMINDERS',
}

export enum FocusedEventSourcesEnum {
  CALENDAR_PREVIEW = 'CALENDAR_PREVIEW',
  CALENDAR_GRID = 'CALENDAR_GRID',
  MORE_EVENTS_POPOVER = 'MORE_EVENTS_POPOVER',
}

const RCalendarEventOverrides = t.partial({
  summary: t.union([t.string, t.undefined]),
  startEnd: t.union([RCalendarEventVersionStartEnd, t.undefined]),
  color: t.union([makeEnumRuntimeType(PaletteColorsEnum), t.undefined]),
});

export const RCalendarEventParameters = t.type({
  calendarEventVersionUid: RCalendarEventVersionUidRef,
  calendarVersionId: RCalendarVersionIdRef,
  instanceStartDate: t.union([t.Integer, t.undefined]),
});

const RCalendarEventGridParameters = t.intersection([
  RCalendarEventParameters,
  t.type({
    // Differentiate between events that were focused via the calendar preview widget and the calendar grid.
    source: makeEnumRuntimeType(FocusedEventSourcesEnum),
    // Property overrides that affect the way this event should be displayed on the calendar grid.
    overrides: RCalendarEventOverrides,
  }),
]);

const RViewingNowCalendar = t.type({
  id: t.string,
  address: t.string,
  calendarVersionId: t.union([RCalendarVersionIdRef, t.undefined]),
  status: makeEnumRuntimeType(AsyncStatusesEnum),
  color: makeEnumRuntimeType(PaletteColorsEnum),
});

export const RCalendarsPanelState = t.intersection([
  RPanelStateBase,
  t.type({
    id: RCalendarsPanelId,
    type: t.literal(PanelTypesEnum.CALENDARS),
    parameters: RCalendarsParameters,
    status: RAsyncStatus,
    calendarEventVersionUids: t.readonlyArray(RCalendarEventVersionUidRef),
    maxUpdatedAt: t.union([t.number, t.undefined]),
    cancelTokenId: RPanelCancelTokenId,
    highlightedCalendarEventParameters: t.union([RCalendarEventGridParameters, t.undefined]),
    focusedCalendarEventParameters: t.union([RCalendarEventGridParameters, t.undefined]),
    popoverCalendarEventParameters: t.union([RCalendarEventGridParameters, t.undefined]),
    viewingNowCalendars: t.readonlyArray(RViewingNowCalendar),
    scheduling: t.union([RCalendarsPanelSchedulingState, t.undefined]),
  }),
]);

/*
 * Types.
 */

export interface CalendarEventParameters extends Readonly<t.TypeOf<typeof RCalendarEventParameters>> {}
export interface CalendarEventGridParameters
  extends Readonly<t.TypeOf<typeof RCalendarEventGridParameters>> {}
export interface CalendarEventOverrides extends Readonly<t.TypeOf<typeof RCalendarEventOverrides>> {}
export interface ViewingNowCalendar extends Readonly<t.TypeOf<typeof RViewingNowCalendar>> {}

export interface CalendarsPanelState extends Readonly<t.TypeOf<typeof RCalendarsPanelState>> {}
